// SEAR / Searcys order peculiarities (London Smoke & Cure).
// Trigger when account code (users.username) starts with SEAR **or** Pricing key
// starts with "searcy" (e.g. searcy's, searcys):
//   - source_id = "{baseOrderNumber} - {companyName}"
//   - top-level customer_id = "Searcys" unless users.customXeroId is set
//   - checkout_data["Customer ID"] = "Searcys" unless users.customXeroId is set
// Optional admin-only users.customXeroId overrides **both** top-level customer_id
// and checkout_data["Customer ID"] (e.g. Searcys Events -> "Searcys Events").
// All other customers and all other order fields stay unchanged.

export const SEARCYS_CHECKOUT_CUSTOMER_ID = 'Searcys';

/** @param {unknown} code */
export function isSearcysAccountCode(code) {
    return /^SEAR/i.test(String(code || '').trim());
}

/**
 * Product Management pricing list key for Searcys (e.g. "searcy's", "searcys").
 * @param {unknown} raw
 */
export function isSearcysPricingKey(raw) {
    const key = String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
    if (!key) return false;
    // Apostrophe / punctuation stripped for match: searcy's -> searcy's still starts with searcy
    const compact = key.replace(/[''`´]/g, '');
    return compact.startsWith('searcy');
}

/**
 * @param {{ accountCode?: string, pricingKey?: string }} args
 */
export function shouldApplySearcysOrderPeculiarity({ accountCode, pricingKey } = {}) {
    return isSearcysAccountCode(accountCode) || isSearcysPricingKey(pricingKey);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeCustomXeroId(value) {
    return String(value || '').trim();
}

/**
 * @param {string} baseId
 * @param {string} companyName
 * @param {{ inlinePrefix?: string, trailingSuffix?: string }} [affixes]
 *   Place-on-behalf only: optional segment before company / after company.
 *   Standing orders omit these — behaviour unchanged.
 * @returns {string}
 */
export function buildSearcysSourceId(baseId, companyName, affixes = {}) {
    const base = String(baseId || '').trim();
    const company = String(companyName || '').trim();
    const inlinePrefix = String(affixes.inlinePrefix || '').trim();
    const trailingSuffix = String(affixes.trailingSuffix || '').trim();

    // Place-on-behalf affixes: base - [prefix -] company [- suffix]
    if (inlinePrefix || trailingSuffix) {
        const parts = [];
        if (base) parts.push(base);
        if (inlinePrefix) parts.push(inlinePrefix);
        if (company) parts.push(company);
        if (trailingSuffix) parts.push(trailingSuffix);
        return parts.join(' - ');
    }

    if (!base) return company ? ` - ${company}` : '';
    if (!company) return base;
    const suffix = ` - ${company}`;
    if (base.endsWith(suffix) || base.toLowerCase().endsWith(suffix.toLowerCase())) {
        return base;
    }
    // Already has " - Something" — do not double-append when company matches trailing segment.
    const dashIdx = base.lastIndexOf(' - ');
    if (dashIdx > 0) {
        const trailing = base.slice(dashIdx + 3).trim();
        if (trailing.toLowerCase() === company.toLowerCase()) return base;
    }
    return `${base}${suffix}`;
}

/**
 * Resolve Customer ID for checkout_data and (when custom) top-level customer_id.
 * Custom Xero ID wins when set; else SEAR account / searcy pricing -> Searcys; else null.
 *
 * @param {{ accountCode?: string, pricingKey?: string, customXeroId?: string }} args
 * @returns {string|null}
 */
export function resolveCheckoutCustomerId({ accountCode, pricingKey, customXeroId } = {}) {
    const custom = normalizeCustomXeroId(customXeroId);
    if (custom) return custom;
    if (shouldApplySearcysOrderPeculiarity({ accountCode, pricingKey })) {
        return SEARCYS_CHECKOUT_CUSTOMER_ID;
    }
    return null;
}

/**
 * Apply SEAR source_id / customer_id, plus optional Custom Xero ID override on both
 * top-level customer_id and checkout_data["Customer ID"].
 * Keeps `id` as the bare base order number; only `source_id` gets the company suffix.
 *
 * @param {{
 *   accountCode?: string,
 *   pricingKey?: string,
 *   companyName?: string,
 *   baseOrderId?: string,
 *   customXeroId?: string,
 *   sourceIdInlinePrefix?: string,
 *   sourceIdTrailingSuffix?: string,
 *   order: object,
 * }} args
 * @returns {object}
 */
export function applySearcysOrderFields({
    accountCode,
    pricingKey,
    companyName,
    baseOrderId,
    customXeroId,
    sourceIdInlinePrefix,
    sourceIdTrailingSuffix,
    order,
}) {
    if (!order || typeof order !== 'object') return order;

    const isSear = shouldApplySearcysOrderPeculiarity({ accountCode, pricingKey });
    const custom = normalizeCustomXeroId(customXeroId);
    const resolvedCustomerId = resolveCheckoutCustomerId({
        accountCode,
        pricingKey,
        customXeroId,
    });

    if (!isSear && !resolvedCustomerId) return order;

    let next = { ...order };

    if (isSear) {
        const base =
            String(baseOrderId || order.id || '').trim() ||
            String(order.source_id || '').trim();
        const company = String(companyName || order.customer?.company || '').trim();
        const sourceId = buildSearcysSourceId(base, company, {
            inlinePrefix: sourceIdInlinePrefix,
            trailingSuffix: sourceIdTrailingSuffix,
        });
        next = {
            ...next,
            id: base || order.id,
            source_id: sourceId,
            customer_id: custom || SEARCYS_CHECKOUT_CUSTOMER_ID,
        };
        if (
            next.checkout_data &&
            typeof next.checkout_data === 'object' &&
            Object.prototype.hasOwnProperty.call(next.checkout_data, 'Source ID#')
        ) {
            next = {
                ...next,
                checkout_data: {
                    ...next.checkout_data,
                    'Source ID#': sourceId,
                },
            };
        }
    } else if (custom) {
        next = {
            ...next,
            customer_id: custom,
        };
    }

    if (resolvedCustomerId) {
        const checkoutBase =
            next.checkout_data && typeof next.checkout_data === 'object'
                ? next.checkout_data
                : {};
        next = {
            ...next,
            checkout_data: {
                ...checkoutBase,
                'Customer ID': resolvedCustomerId,
            },
        };
    }

    return next;
}
